import {Component, inject, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {MatInputModule} from '@angular/material/input';
import {MatFormFieldModule} from '@angular/material/form-field';
import {AuthenticationFormFactory} from '../../factories/authentication-form.factory';
import {MatIconModule} from '@angular/material/icon';
import {TranslocoPipe, TranslocoService} from '@jsverse/transloco';
import {MatButtonModule} from '@angular/material/button';
import {IconButtonComponent} from '../../../../shared/components/icon-button/icon-button.component';
import {TextButton} from '../../../../shared/components/text-button/text-button.component';
import {AuthenticationService} from '../../authentication.service';
import {LoginResponseDto} from '../../dtos/login-response.dto';
import {MemoryService} from '../../../../core/services/memory.service';
import {Router} from '@angular/router';
import {MatDialog} from '@angular/material/dialog';
import {ResetPasswordDialog} from '../../dialogs/reset-password/reset-password.dialog';
import {HttpErrorResponse} from '@angular/common/http';
import {ErrorService} from '../../../../core/services/error.service';
import {RedirectService} from '../../../../core/services/redirect.service';
import {WindowReferenceService} from '../../../../core/services/window.service';

@Component({
  selector: 'app-login',
  imports: [ReactiveFormsModule, MatFormFieldModule, MatInputModule, MatIconModule, TranslocoPipe, MatButtonModule, IconButtonComponent, TextButton],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss'
})
export class LoginComponent implements OnInit {
  fb: FormBuilder = inject(FormBuilder);
  authenticationService: AuthenticationService = inject(AuthenticationService);
  memoryService: MemoryService = inject(MemoryService);
  redirectService: RedirectService = inject(RedirectService);
  translocoService = inject(TranslocoService);
  router: Router = inject(Router);
  dialog: MatDialog = inject(MatDialog);
  errorService: ErrorService = inject(ErrorService);
  windowSerivce = inject(WindowReferenceService);
  loginForm!: FormGroup;
  hidePassword: boolean = true;
  error?: string;

  ngOnInit(): void {
    this.loginForm = AuthenticationFormFactory.createLoginForm(this.fb);
  }

  togglePasswordVisibility() {
    this.hidePassword = !this.hidePassword;
  }

  login() {
    this.loginForm.markAllAsTouched();
    if (this.loginForm.invalid) {
      return;
    }

    let loginRequestDto = this.loginForm.value;
    loginRequestDto.locationId = this.memoryService.locationId ?? 0;
    this.authenticationService.login(loginRequestDto).subscribe({
      next: (loginResponseDto: LoginResponseDto) => {
        this.error = undefined;
        this.memoryService.accessToken = loginResponseDto.accessToken;
        this.memoryService.patientId = loginResponseDto.patientId;
        this.memoryService.refreshToken = loginResponseDto.refreshToken;
        this.memoryService.isNewUser = loginResponseDto.isNew;
        this.memoryService.patientEmail = loginRequestDto.email;
        if (this.windowSerivce.pushEvents) {
          var payload = {event: 'login_completed'};
          this.windowSerivce.push(payload);
        }

        const redirectPayload = this.redirectService.consume();
        if (redirectPayload) {
          this.router.navigateByUrl(redirectPayload.url, {state: redirectPayload.state});
        }
      },
      error: (error: HttpErrorResponse) => {
        if (error.status === 401) {
          this.error = this.translocoService.translate('login.error.unauthorized');
          return;
        }

        this.error = undefined;
        this.errorService.handleError(error, this.translocoService.translate('error.title.login'));
      }
    })
  }

  register() {
    if (!this.redirectService.isAvailable()) {
      const url = this.router.url;
      const state = history.state;
      this.redirectService.set({url: url, state: state});
    }

    this.router.navigate(['/register']);
  }

  resetPassword() {
    this.dialog.open(ResetPasswordDialog, {autoFocus: false, data: {email: this.loginForm.value.email}});
  }
}
